/**
 * @module app.core
 */

import { autoinject, observable, BindingEngine, Disposable, ICollectionObserverSplice } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/essette-api';
import { TaskService } from 'app/core/services/task-service';
import { ITasksConfig, IQueuesConfig, IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { McgInformedService } from 'app/ext/mcg-informed/services/mcg-informed-service';
import { I18N } from 'aurelia-i18n';
import { TaskWithLocalState } from 'app/core/models/local-state-extensions';
import { getReference } from 'app/shared/models/essette-types';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { TransitionalManager } from 'app/shared/lib/transitional/manager';
import { AddTasksSelected as SelectedTasks } from 'app/core/components/tasks/add-tasks-selected';
import { EssAutocompleteCustomElement } from 'app/shared/elements/forms/ess-autocomplete';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { createQueryResults } from 'app/shared/lib/api/api-utilities';
import { guid } from 'app/shared/lib/guid';
import { pushCopy, spliceCopy } from 'app/shared/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';


@autoinject()
export class AddTasks extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.stringNull
    protected targetId: string | null = null;

    @bindable.objectNull
    protected tasksConfig: ITasksConfig | null = null;

    @bindable.objectNull
    protected queuesConfig: IQueuesConfig | null = null;

    @bindable.objectNull
    protected assessmentsConfig: IAssessmentsConfig | null = null;

    @bindable.objectNull
    protected target: CM.CarePlanGoal | null = null;

    /** the value of the autocomplete textbox */
    @observable
    public taskSearchQuery: string | null = null;

    protected currentTasks: Core.Task[] = [];

    private selectedTasksSubscription: Disposable | null = null;
    private selectedRecommendedTasksSubscription: Disposable | null = null;

    /** Holds the value of the selected radio input. Switches between how user finds a task */
    protected taskOption: string = 'selectTasks';

    /** task definition lookup (select and autocomplete results) */
    protected taskDefinitions: Core.TaskDefinition[] = [];
    protected recommendedTasks: Core.TaskDefinition[] = [];

    /** array of search results from API for autocomplete (not currently used) */
    protected taskSearchResults: Core.TaskDefinition[] = [];

    /** array of tasks user has selected from either the multi-select or autocomplete */
    protected selectedTasks: Core.TaskDefinition[] = [];
    protected selectedRecommendedTasks: Core.TaskDefinition[] = [];

    /** Array of tasks the users has selected (standard and custom) to be saved */
    protected insertTasks: TaskWithLocalState[] = [];

    /** the value of the custom task input */
    protected customTaskText: string | null = null;
    protected customTaskTextMax: number = 200;

    /** Permission that controls whether a user can enter a custom task */
    protected hasAddCustomTasks: boolean = false;
    /** Permission that controls whether a custom task can be added to the library (N/A to interventions) */
    protected hasAddCustomTaskToLibrary: boolean = false;

    protected id: guid = guid();

    ////****************** Element References ***************************************////
    protected $autocomplete!: EssAutocompleteCustomElement;
    protected $selectedTasks!: SelectedTasks;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly taskService: TaskService,
        private readonly assessmentService: AssessmentService,
        private readonly mcgService: McgInformedService,
        private readonly bindingEngine: BindingEngine,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        protected readonly transitional: TransitionalManager,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(): void {
        if(!(this.targetId || this.target)) { throw new Error('Bindable property [target-id] was not set'); }
        if(!this.tasksConfig) { throw new Error('Bindable property [tasks-config] was not set'); }
        if(!this.queuesConfig) { throw new Error('Bindable property [queues-config] was not set'); }

        const targetType = this.tasksConfig.targetType;
        if(!targetType) { throw new Error('[tasks-config] did not define a targetType'); }
        this.taskService.configure(this.tasksConfig);

        this.checkPermissions();
        this.loadTaskDefinitions();
        if(!this.targetId && this.target?.id) this.targetId = this.target.id;
        this.loadCurrentTasks();

        if(targetType === 'CM.CarePlanGoal') {
            this.loadRecommended();
        }
    }

    protected attached(): void {
        this.observeSelectedTasks();
        if(this.tasksConfig?.targetType === 'CM.CarePlanGoal') {
            this.observeSelectedRecommendedTasks();
        }
    }

    protected detached(): void {
        if(this.selectedTasksSubscription) {
            this.selectedTasksSubscription.dispose();
        }

        if(this.selectedRecommendedTasksSubscription) {
            this.selectedRecommendedTasksSubscription.dispose();
        }
    }


    ////****************** Events ***************************************************////

    /** When selectedTasks array changes (select tasks or search tasks), the detected changes are added/removed from insertTasks (the tasks to be saved) */
    protected selectedTasksChanged(splices: ICollectionObserverSplice<Core.TaskDefinition>[]): void {
        if(this.insertTasks.length === 0 && this.selectedTasks.length === 0) return;
        this.updateTasksAdded(splices, this.selectedTasks);
    }

    protected selectedRecommendedTasksChanged(splices: ICollectionObserverSplice<Core.TaskDefinition>[]): void {
        if(this.insertTasks.length === 0 && this.selectedRecommendedTasks.length === 0) return;
        this.updateTasksAdded(splices, this.selectedRecommendedTasks);
    }

    protected targetChanged(): void {
        if(!this.target?.id) return;
        // reload recommended interventions only if target is new
        if(this.target.id !== this.targetId && this.tasksConfig?.targetType === 'CM.CarePlanGoal') {
            this.loadRecommended();
        }
        this.targetId = this.target.id;
        this.loadCurrentTasks();
    }

    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        const targetType = this.tasksConfig?.targetType;
        if(!targetType) { return; }

        await this.accountContext.load();
        if(targetType === 'CM.Case') {
            this.hasAddCustomTasks = this.accountContext.hasPermission('CM.CaseTask.Insert.AddCustom');
            this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CaseTask.Insert.AddToLibrary');
        }
        else if(targetType === 'CM.CarePlanGoal') {
            this.hasAddCustomTasks = this.accountContext.hasPermission('CM.CarePlanIntervention.Insert.AddCustom');
            this.hasAddCustomTaskToLibrary = this.accountContext.hasPermission('CM.CarePlanIntervention.Insert.AddToLibrary');
        }
    }
    private observeSelectedTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedTasksSubscription) {
            this.selectedTasksSubscription.dispose();
        }
        this.selectedTasksSubscription = this.bindingEngine
            .collectionObserver(this.selectedTasks) // observe the selectedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(this.selectedTasksChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedTasksChanged method

        // subscription disposed of in detached()
    }

    private observeSelectedRecommendedTasks(): void {
        // observer only works on a modified array. if the array is replaced, the observer will not function for it any longer unless subscribed again
        if(this.selectedRecommendedTasksSubscription) {
            this.selectedRecommendedTasksSubscription.dispose();
        }
        this.selectedRecommendedTasksSubscription = this.bindingEngine
            .collectionObserver(this.selectedRecommendedTasks) // observe the selectedRecommendedTasks array, which changes in the multi-select Task Definition Select element
            .subscribe(this.selectedRecommendedTasksChanged.bind(this)); // pass the splice information (addedCount, index, removed) to selectedRecommendedTasksChanged method

        // subscription disposed of in detached()
    }

    private updateTasksAdded(splices: ICollectionObserverSplice<Core.TaskDefinition>[], selectedArray: Core.TaskDefinition[]): void {
        for(var i = 0; i < splices.length; i++) {
            var splice = splices[i];

            var tasksAdded = selectedArray.slice(splice.index, splice.index + splice.addedCount);
            if(tasksAdded.length > 0) {
                tasksAdded.forEach(
                    task => {
                        const t: TaskWithLocalState = {
                            isCustom: false,
                            description: task.description,
                            definition: {
                                id: task.id,
                                type: task.type,
                                disallowActivation: task.disallowActivation
                            },
                            delegated: {
                                queue: getReference(task.defaultQueue)
                            },
                            _state: { isSaved: false, addToLibrary: false }
                        };
                        //TODO don't just swallow this error do something about it
                        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(t); }
                        this.insertTasks = pushCopy(this.insertTasks, t);
                    });
            }

            if(splice.removed.length) {
                (splice.removed as Core.TaskDefinition[]).forEach(
                    task => {
                        const i = this.insertTasks.find(t => (!!t.definition && t.definition.id) === task.id);
                        if(i) {
                            this.insertTasks = spliceCopy(this.insertTasks, this.insertTasks.indexOf(i), 1);
                        }
                    });
            }
        }

        if(this.insertTasks.length === 0) this.$autocomplete?.clear();
    }

    public get valid(): boolean {
        return this.insertTasks.length > 0 && !this.$selectedTasks.hasInvalidQueue;
    }

    protected getSelectText(): string {
        if(this.i18n) {
            const { length } = this.selectedTasks;
            if(length > 1) {
                // INCOMPLETE: this doesn't seem to use the i18n pluralization functions properly
                let itemString = this.i18n.tr(`${this.tasksConfig!.labelKey}_plural`);
                return this.i18n.tr('ui:labels.selected', { what: itemString, number: length, count: length });
            }
        }
        return '';
    }

    /** Called when a user clicks on an auto-complete search result */
    protected addAutoCompleteTask(task: Core.TaskDefinition): void {
        this.selectedTasks.push(task); // will trigger selectedTasksChanged(), and add the task to selectedTasks
        this.taskSearchQuery = '';

        this.taskSearchResults.splice(0); // clear search results (and thus hide the auto-complete result list)
    }

    protected isCustomTaskTextValid(text: string): boolean {
        return !!(text && /[^\s]/.test(text));
    }

    /** Called when a user types in a custom task description and clicks the button */
    protected addCustomTask(): void {
        const task: TaskWithLocalState = {
            isCustom: true,
            description: this.customTaskText,
            _state: { isSaved: false, addToLibrary: false }
        };
        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(task) };

        if(!this.isCustomTaskTextValid(this.customTaskText || '')) {
            return;
        }
        else {
            this.insertTasks = pushCopy(this.insertTasks, task);
        }

        this.customTaskText = null;
    }

    public navigateBack(refresh: boolean = false): void {
        if(this.tasksConfig && this.tasksConfig.backRouteName) {
            const params = refresh ? { refresh: 'true' } : {};
            this.router.navigateToRoute(this.tasksConfig.backRouteName, params);
        } else {
            this.router.navigateBack();
        }
    }

    protected filterTasks = (task: Core.Task): boolean => {
        if(this.selectedTasks.some(t => t.id === task.id)) {
            return false;
        }
        if(this.tasksConfig && this.tasksConfig.allowDuplicates) { return true; }
        if(!this.currentTasks && !task) { return false; }
        return !this.currentTasks.some(t => task.id === (t.definition && t.definition.id));
    }

    /** Takes a task from the insertTasks grid and constructs a new copy to be sent to the API */
    protected buildSaveTask(task: TaskWithLocalState): TaskWithLocalState {
        if(!task._state) { task._state = {}; }

        const saveTask: TaskWithLocalState = {
            isCustom: task.isCustom,
            due: !!task.due && !!task.due.date ? { date: task.due.date } : undefined,
            definition: getReference(task.definition),
        };

        // only add description to request if task is custom
        if(task.isCustom) saveTask.description = task.description;

        if(this.hasAddCustomTaskToLibrary && !!task._state.addToLibrary) {
            // The API will add a task to the library if the task doesn't have a definition and "isCustom = false"
            saveTask.isCustom = false;
            delete saveTask.definition;
        }

        if(task.delegated && task.delegated.queue) {
            saveTask.delegated = {
                queue: getReference(task.delegated.queue),
                to: task.delegated.to && task.delegated.queue && task.delegated.queue.delegated ? {
                    id: task.delegated.to.id,
                    type: task.delegated.queue.delegated.type
                } : undefined
            }
        }

        if(this.taskService.config.addTargetProperty) { this.taskService.config.addTargetProperty(saveTask) };

        return saveTask;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }
    public submittedChanges?: any;

    public async submit(): Promise<boolean> {
        return await this.saveTasks();
    }

    public async reset(): Promise<void> {
        this.selectedTasks.splice(0);
        this.selectedRecommendedTasks.splice(0);
        this.insertTasks.splice(0);
        this.taskSearchQuery = '';
        this.taskSearchResults.splice(0);
        this.taskOption = 'selectTasks';
        this.$autocomplete?.clear();
    }


    ////****************** Service Calls ********************************************////
    /** Iterates through the tasks and saves each one. */
    @apiCall
    private async saveTasks(): Promise<boolean> {
        const duplicateTasks: string[] = [];

        const promises = new Array<Promise<boolean>>();

        let unsavedTasks =
            this.insertTasks
                .filter(t => {
                    // If user clicks save a second time, we skip tasks that might have been saved already.
                    return !(t._state && t._state.isSaved);
                });

        unsavedTasks
            .forEach(t => {
                if(this.tasksConfig && this.tasksConfig.allowDuplicates && this.currentTasks.some(ct => (ct.definition && ct.definition.id) === (t.definition && t.definition.id))) {
                    duplicateTasks.push(t.description || '');
                }
            });

        /** Show confirmation to add duplicate tasks */
        if(duplicateTasks.length > 0) {
            const message: string = this.i18n.tr('ui:messages.duplicate.confirm', { count: duplicateTasks.length, what: this.i18n.tr(this.tasksConfig!.labelKey, { count: duplicateTasks.length }) });
            const result = await confirmation.showCustom(message, duplicateTasks.join('\n'), confirmation.options.StandardConfirm);
            if(result.dialogResponse === confirmation.options.CANCEL) {
                //canceling means we just stop
                return Promise.resolve(false);
            }

        }

        try {
            this.state.beginSave();

            unsavedTasks
                .forEach(t => {
                    promises.push(this.saveSingleTask(t));
                });

            const responses = await Promise.all(promises);

            if(responses.every(r => r)) {

                this.logger.info("Add tasks successful");
                return Promise.resolve(true);

            }
            return Promise.resolve(false);
        } catch(error) {
            this.toaster.standard.saved('core:task.$', false);
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async saveSingleTask(task: Core.Task): Promise<boolean> {
        const saveTask = this.buildSaveTask(task);

        if(task.definition?.type?.code === 'Core.Assessment' && task.definition?.type?.argument?.id) {
            const externalSystemId = await this.loadAssessmentExternalSystemId(task.definition.type.argument.id);
            if(externalSystemId) saveTask.externalSystem = { id: externalSystemId };
        }

        const taskResponse = await this.taskService.insertTask(saveTask);
        let externalContentSuccess = true;

        if(taskResponse.isSuccessful) {
            const taskId = taskResponse.id;

            if((task.definition?.type?.code === "Core.CorrespondenceLetter" && saveTask.due === undefined && !task.definition?.disallowActivation) || (task.definition?.type?.argument?.type?.code === "Core.CorrespondenceTemplate" && saveTask.due === undefined && !task.definition?.disallowActivation)) {
                const newLetter: Core.CorrespondenceLetter = {
                    template: {
                        id: task.definition.type.argument?.id
                    },
                    task: { id: taskId }
                };

                if(this.tasksConfig?.addTargetProperty) {
                    this.tasksConfig?.addTargetProperty(newLetter);
                }

                try {
                    const letterResponse = await this.taskService.insertSmartTaskLetter(newLetter);
                    if(letterResponse.isSuccessful && letterResponse.id) {
                        this.toaster.standard.saved('core:correspondenceLetter.$', true);
                        try {
                            const snippetResponse = await this.transitional.post(`?action=generateLetter&LetterID=${letterResponse.id}&ModuleCode=${this.tasksConfig?.moduleCode}`);
                            this.toaster.standard.saved('core:correspondenceSnippet.$', snippetResponse.isSuccessful)
                        } catch(error) {
                            this.toaster.error().message(this.i18n.tr('core:correspondenceLetter.notifications.failedSnippets')).show();
                        }
                    }
                }
                catch(error) {
                    this.toaster.standard.saved('core:correspondenceLetter.$', false);
                }
            }

            if(task.definition?.type?.code === 'Core.Assessment' && saveTask.externalSystem?.id) {
                try {
                    externalContentSuccess = await this.saveAssessmentExternalContent(taskId);
                }
                catch(error) {
                    throw error;
                }
            }
        }

        return taskResponse.isSuccessful && externalContentSuccess;
    }

    @apiCall
    private async loadTaskDefinitions(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'defaultQueue',
            'type',
            'type.argument',
            'type.argument.type'
        ];

        const fields = [
            'defaultQueue.id',
            'description',
            'disallowActivation',
            'type.argument.id',
            'type.argument.type.code',
            'type.code'
        ];

        try {
            this.state.beginLoad();

            const taskDefinitions = await this.taskService.loadTaskDefinitions(options, includes, fields);
            this.taskDefinitions = taskDefinitions.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    public async loadCurrentTasks(): Promise<void> {
        if(!this.targetId) return;

        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'definition',
            'externalSystem'
        ];

        const fields = [
            'definition.id',
            'externalIdentifier',
            'externalSystem.code'
        ];

        try {
            this.state.beginLoad();
            const response = await this.taskService.loadTasksByTarget(this.targetId, options, includes, true, fields);
            this.currentTasks = response.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    /** Called as user types into the auto-complete task search box */
    @apiCall
    protected async searchTasks(newValue: string): Promise<void> {
        if(!newValue) {
            if(this.taskSearchResults) this.taskSearchResults.splice(0);
            return;
        }

        const includes = [
            'defaultQueue',
            'type',
            'type.argument',
            'type.argument.type'
        ];

        const fields = [
            'defaultQueue.id',
            'description',
            'disallowActivation',
            'type.argument.id',
            'type.argument.type.code',
            'type.code'
        ];

        try {
            this.state.beginLoad();
            const response = await this.taskService.searchDefinitions(newValue, undefined, includes, fields);
            this.taskSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadRecommended(): Promise<void> {
        const options: Partial<IQueryLoadOptions> = {
            all: true
        };

        const includes = [
            'defaultQueue',
            'type',
            'type.argument',
            'type.argument.type'
        ];

        const fields = [
            'defaultQueue.id',
            'description',
            'disallowActivation',
            'type.argument.id',
            'type.argument.type.code',
            'type.code'
        ];

        try {
            this.state.beginLoad();
            const recommended = await (this.target?.definition?.id
                ? this.taskService.loadRecommendedInterventions(this.target.definition.id, options, includes, fields)
                : createQueryResults('Core.TaskDefinition', [], true));

            this.recommendedTasks = recommended.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async loadAssessmentExternalSystemId(assessmentId: string): Promise<string | null> {
        let externalSystemId = null;
        try {
            this.state.beginLoad();

            if(!this.assessmentsConfig) return null;
            this.assessmentService.configure(this.assessmentsConfig);

            const includes = ['externalSystem'];
            const fields = ['externalSystem.id'];

            const response = await this.assessmentService.loadAssessment(assessmentId, includes, fields);
            externalSystemId = response.results[0].externalSystem?.id || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
            return externalSystemId;
        }
    }

    @apiCall
    private async saveAssessmentExternalContent(taskId: string | null): Promise<boolean> {
        if(!taskId) return false;

        try {
            this.state.beginSave();

            const attachmentId = await this.loadAssessmentAttachment(taskId);
            if(!attachmentId) return false;

            const content = await this.loadMcgAssessmentContent(attachmentId);
            if(!content) return false;

            const externalContent: Core.AssessmentAttachmentExternalContent = {
                assessmentAttachment: { id: attachmentId },
                externalContentType: { code: 'AssessmentJson' },
                content: JSON.stringify(content),
                mediaType: { code: 'data.json' }
            };

            const resposne = await this.assessmentService.insertAttachmentExternalContent(externalContent);
            if(resposne.isSuccessful) return true;

            return false;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

    @apiCall
    private async loadAssessmentAttachment(taskId: string): Promise<string | null> {
        let attachmentId = null;
        try {
            this.state.beginLoad();

            const response = await this.assessmentService.loadAttachmentByTask(taskId, undefined, undefined, ['id']);
            attachmentId = response.results[0].id || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
            return attachmentId;
        }
    }

    @apiCall
    private async loadMcgAssessmentContent(attachmentId: string): Promise<string | null> {
        let content = null;
        try {
            this.state.beginLoad();

            const response = await this.mcgService.getAssessmentContent(attachmentId);
            content = response.result || null;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
            return content;
        }
    }
}
